import { CPRState, ArchivedCPR } from './types';

// Function to get prefix from URL
const getAppPrefix = (): string | null => {
    const pathParts = window.location.pathname.split('/').filter(part => part);
    // The app name should be the second part of the path: /[hospital-name]/[app-name]/...
    if (pathParts.length >= 2) {
      return pathParts[1];
    }
    return "";
};

// Base key names without prefix
const BASE_CURRENT_KEY = 'current_cpr_state_v4';
const BASE_ARCHIVED_KEY = 'archived_cprs_v4';
const MAX_ARCHIVED_CPRS = 5;
const HOURS_24 = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Functions to get prefixed keys
const getCurrentKey = (): string => {
  const prefix = getAppPrefix();
  return prefix ? `${prefix}_${BASE_CURRENT_KEY}` : BASE_CURRENT_KEY;
};

const getArchivedKey = (): string => {
  const prefix = getAppPrefix();
  return prefix ? `${prefix}_${BASE_ARCHIVED_KEY}` : BASE_ARCHIVED_KEY;
};

export const saveCurrentState = (state: Partial<CPRState>, section?: keyof CPRState): void => {
  try {
    const currentState = (loadCurrentState() || {}) as Partial<CPRState>;
    
    let newState: Partial<CPRState>;
    
    // If a specific section is provided, only update that section
    if (section) {
      newState = {
        ...currentState,
        [section]: {
          ...(typeof currentState[section] === 'object' ? currentState[section] : {}),
          ...(typeof state[section] === 'object' ? state[section] : {})
        }
      };
    } else {
      // Otherwise merge the entire state
      newState = {
        ...currentState,
        ...state
      };
    }

    // Update lastSaved timestamp
    newState.lastSaved = new Date().toISOString();

    localStorage.setItem(getCurrentKey(), JSON.stringify(newState));
  } catch (error) {
    console.error('Error saving current CPR state:', error);
  }
};

export const loadCurrentState = (): CPRState | null => {
  try {
    const savedState = localStorage.getItem(getCurrentKey());
    if (!savedState) {
      return null;
    }

    const state = JSON.parse(savedState) as CPRState;
    
    // Check if 24 hours have passed since last save
    if (state.lastSaved) {
      const lastSaved = new Date(state.lastSaved).getTime();
      const now = new Date().getTime();
      
      if (now - lastSaved > HOURS_24) {        
        clearCurrentState(); // Clear expired state
        return null;
      }
    }

    return state;
  } catch (error) {
    console.error('Error loading current CPR state:', error);
    return null;
  }
};

export const clearCurrentState = (): void => {
  archiveCPRState();
  localStorage.removeItem(getCurrentKey());
};

export const archiveCPRState = (): void => {
  try {    
    const currentState = loadCurrentState();
    if (currentState) {
      const archiveKey = getArchivedKey();
      const archives = JSON.parse(localStorage.getItem(archiveKey) || '[]');
      const timestamp = new Date().toISOString();
      const archivedState = {
        ...currentState,
        archivedAt: timestamp,
        resusStartTime: currentState.startTime,
        id: `${getAppPrefix()}-${timestamp}`,
        outcome: currentState.endState
      };
      const updatedArchives = [archivedState, ...archives].slice(0, MAX_ARCHIVED_CPRS);
      localStorage.setItem(archiveKey, JSON.stringify(updatedArchives));
    }
  } catch (error) {
    console.error('Error archiving CPR state:', error);
  }
};

export const loadArchivedCPRs = (): ArchivedCPR[] => {
  try {    
    const archives = localStorage.getItem(getArchivedKey());
    return archives ? JSON.parse(archives) : [];
  } catch (error) {
    console.error('Error loading archived CPRs:', error);
    return [];
  }
};

export const loadArchivedCPRById = (id: string): CPRState | null => {
  try {    
    const archives = loadArchivedCPRs();
    const archived = archives.find(cpr => cpr.id === id);
    return archived || null;
  } catch (error) {
    console.error('Error loading archived CPR by id:', error);
    return null;
  }
};

export const restoreArchivedCPR = (id: string): boolean => {
  try {
    const archived = loadArchivedCPRById(id);
    if (archived) {
      localStorage.setItem(getCurrentKey(), JSON.stringify(archived));
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error restoring archived CPR:', error);
    return false;
  }
};

export const clearHistory = (): void => {
  try {
    localStorage.setItem(getArchivedKey(), '[]');
  } catch (error) {
    console.error('Error clearing CPR history:', error);
  }
};